import type { User as FirebaseUser } from 'firebase/auth';
import { get, ref as dbRef, update } from 'firebase/database';
import { getDownloadURL, ref as storageRef, uploadString } from 'firebase/storage';

import { auth, db, storage } from './firebase';
import { initials } from '../utils/initials';
import { isDataUrl } from '../utils/isDataURL';
import type { User } from '../types';

const dbRoot = '/plan_c/';

/**
 * JSON scalar values supported by Firebase updates.
 */
export type JsonScalar = string | number | boolean | null;

/**
 * JSON-compatible values supported by active profile writes.
 */
export type JsonValue = JsonScalar | JsonValue[] | { [key: string]: JsonValue };

/**
 * Object map for JSON-compatible values.
 */
export type JsonObject = Record<string, JsonValue>;

/**
 * Flat Firebase update object.
 */
export type FlatJsonObject = Record<string, JsonValue>;

/**
 * Data URL upload request created while preparing profile updates.
 */
export interface ImageUploadRequest {
  /** Flattened update key that should receive the uploaded URL. */
  key: string;
  /** Storage path to upload into. */
  path: string;
  /** Original data URL selected by the user. */
  dataUrl: string;
}

/**
 * Prepared profile update data, split for testability.
 */
export interface PreparedProfileUpdate {
  /** Flattened non-empty profile changes. */
  flattened: FlatJsonObject;
  /** Image uploads needed before writing to Realtime Database. */
  imageUploads: ImageUploadRequest[];
}

const isObject = (input: JsonValue): input is JsonObject => {
  return typeof input === 'object' && input !== null && !Array.isArray(input);
};

/**
 * Flattens nested object keys into Firebase multi-location update paths.
 */
export function stringFlattenKeys(object: JsonObject): FlatJsonObject {
  const result: FlatJsonObject = {};

  for (const [key, child] of Object.entries(object)) {
    if (isObject(child)) {
      const flattenedChild = stringFlattenKeys(child);
      for (const [subkey, value] of Object.entries(flattenedChild)) {
        result[`${key}/${subkey}`] = value;
      }
    } else {
      result[key] = child;
    }
  }

  return result;
}

/**
 * Reads a Firebase Realtime Database path once.
 */
export async function fetchRefOnce(path: string): Promise<unknown> {
  const snapshot = await get(dbRef(db, dbRoot + path));
  return snapshot.val() as unknown;
}

/**
 * Writes a multi-location update below the configured database root.
 */
export async function writeToRef(path: string, data: JsonObject): Promise<void> {
  await update(dbRef(db, dbRoot + path), stringFlattenKeys(data));
}

/**
 * Converts a Firebase auth UID into a MeshOS user id.
 */
export async function firebaseIdToMeshId(firebaseUid: string): Promise<string | null> {
  const value = await fetchRefOnce(`logins/${firebaseUid}`);
  return typeof value === 'string' && value.length > 0 ? value : null;
}

const isPublicProfile = (value: unknown): value is Omit<User, 'id'> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const profile = value as Record<string, unknown>;
  return typeof profile.displayName === 'string'
    && typeof profile.color === 'string'
    && typeof profile.initials === 'string';
};

/**
 * Loads a public user profile by MeshOS user id.
 */
export async function meshIdToPublicProfile(meshId: string): Promise<User> {
  if (meshId.length === 0) {
    throw new Error('missing or empty meshId parameter');
  }

  const profile = await fetchRefOnce(`users_public/${meshId}`);
  if (!isPublicProfile(profile)) {
    throw new Error('Invalid user object.');
  }

  return {
    ...profile,
    id: meshId,
  };
}

/**
 * Loads the MeshOS public profile for a Firebase auth user.
 */
export async function loadAuthProfile(firebaseUser: FirebaseUser | null = auth.currentUser): Promise<User | null> {
  if (!firebaseUser) {
    return null;
  }

  const meshId = await firebaseIdToMeshId(firebaseUser.uid);
  if (!meshId) {
    return null;
  }

  const profile = await meshIdToPublicProfile(meshId);
  return {
    ...profile,
    email: firebaseUser.email ?? profile.email,
  };
}

const imagePathForKey = (key: string, userId: string): string | null => {
  if (key === 'image') {
    return `profilePhotos/${userId}`;
  }
  if (key === 'publisherProfile/image') {
    return `publisherPhotos/${userId}`;
  }
  return null;
};

/**
 * Normalizes a profile change set before uploads and database writes.
 */
export function prepareAuthProfileUpdate(
  original: User,
  modifications: Record<string, unknown>
): PreparedProfileUpdate {
  const flatSource = stringFlattenKeys(modifications as JsonObject);
  const flattened: FlatJsonObject = {};
  const imageUploads: ImageUploadRequest[] = [];

  flatSource.email = '';

  for (const [key, value] of Object.entries(flatSource)) {
    if (value === '') {
      continue;
    }

    let nextValue = value;
    const imagePath = typeof value === 'string' ? imagePathForKey(key, original.id) : null;
    if (imagePath && isDataUrl(value as string)) {
      imageUploads.push({
        key,
        path: imagePath,
        dataUrl: value as string,
      });
    } else if (key === 'publisherProfile/slug' && typeof value === 'string') {
      nextValue = value.toLowerCase();
    }

    flattened[key] = nextValue;

    if (key === 'displayName' && typeof value === 'string') {
      flattened.initials = initials(value);
    }
  }

  return {
    flattened,
    imageUploads,
  };
}

/**
 * Builds the Firebase multi-location write object for profile changes.
 */
export function buildAuthProfileWrites(original: User, flattened: FlatJsonObject): JsonObject {
  return {
    users_public: {
      [original.id]: flattened,
    },
  };
}

const resizeAvatarDataUrl = async (dataUrl: string): Promise<string> => {
  if (typeof Image === 'undefined' || typeof document === 'undefined') {
    return dataUrl;
  }

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error('Unable to load selected image.'));
    element.src = dataUrl;
  });

  const size = Math.min(image.naturalWidth || image.width, image.naturalHeight || image.height);
  const sourceX = Math.max(0, ((image.naturalWidth || image.width) - size) / 2);
  const sourceY = Math.max(0, ((image.naturalHeight || image.height) - size) / 2);
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const context = canvas.getContext('2d');

  if (!context) {
    return dataUrl;
  }

  context.drawImage(image, sourceX, sourceY, size, size, 0, 0, 256, 256);
  return canvas.toDataURL('image/jpeg', 0.88);
};

/**
 * Uploads a data URL image into Firebase Storage and returns its public URL.
 */
export async function uploadProfileImage(path: string, dataUrl: string): Promise<string> {
  const resized = await resizeAvatarDataUrl(dataUrl);
  const imageRef = storageRef(storage, path);
  await uploadString(imageRef, resized, 'data_url');
  return await getDownloadURL(imageRef);
}

/**
 * Updates the authenticated user's public MeshOS profile.
 */
export async function updateAuthProfile(
  original: User,
  modifications: Record<string, unknown>
): Promise<void> {
  const prepared = prepareAuthProfileUpdate(original, modifications);
  const flattened = { ...prepared.flattened };

  for (const upload of prepared.imageUploads) {
    flattened[upload.key] = await uploadProfileImage(upload.path, upload.dataUrl);
  }

  await writeToRef('', buildAuthProfileWrites(original, flattened));
}
